import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Pause, Play, ArrowRight, Recycle, Truck, Factory, Trash2, Leaf, Camera, Film, CalendarDays } from "lucide-react";
import { api, mediaUrl, formatWIT, BRAND, CONTENT_TYPE_LABELS } from "@/lib/appApi";
import NewsCard, { DemoBadge } from "@/components/public/NewsCard";
import { Button } from "@/components/ui/button";

const PILLARS = [
  { key: "Pilah", icon: Recycle, desc: "Pemilahan sampah dari sumber" },
  { key: "Angkut", icon: Truck, desc: "Pengumpulan & pengangkutan" },
  { key: "Pengolahan", icon: Factory, desc: "Pengolahan & daur ulang" },
  { key: "Proses Akhir", icon: Trash2, desc: "Pemrosesan akhir di TPA" },
];

function Hero({ headline, headlines, unitMap }) {
  const [paused, setPaused] = useState(false);
  if (!headline) {
    return (
      <div className="relative h-[420px] bg-dlh-green flex items-center justify-center text-center px-4">
        <img src={BRAND.hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative z-10 text-white max-w-2xl">
          <h1 className="font-serif text-4xl sm:text-5xl font-extrabold">PAPA BERSERI NEWS</h1>
          <p className="mt-3 text-dlh-greenLight text-lg">Belum ada berita terbit. Silakan tambahkan melalui dashboard pengelola.</p>
        </div>
      </div>
    );
  }
  const img = headline.featured_media_url ? mediaUrl(headline.featured_media_url) : BRAND.hero;
  const unit = unitMap[headline.responsible_unit];
  return (
    <section className="relative" data-testid="hero-section">
      <Link to={`/berita/${headline.slug}`} className="block relative h-[480px] sm:h-[520px] overflow-hidden group">
        <img src={img} alt={headline.featured_alt || headline.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 sm:via-black/45 to-black/10 sm:to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 pb-7 sm:p-10">
          <div className="max-w-4xl animate-fade-up">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="inline-flex items-center gap-1.5 bg-dlh-orange text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider"><span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />Berita Utama</span>
              {unit && <span className="bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1 rounded-full">{unit.short_name}</span>}
              {headline.is_demo && <DemoBadge article={headline} />}
            </div>
            <h1 className="font-serif text-xl sm:text-5xl font-extrabold text-white leading-[1.15] sm:leading-[1.08] tracking-tight line-clamp-4 sm:line-clamp-none" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.65)" }}>{headline.title}</h1>
            <p className="mt-3 sm:mt-4 text-white/85 text-sm sm:text-lg leading-relaxed line-clamp-2 max-w-2xl">{headline.summary}</p>
            <div className="mt-4 sm:mt-5 flex items-center gap-3 sm:gap-4 flex-wrap">
              <span className="inline-flex items-center gap-2 bg-white text-dlh-green font-bold text-sm px-5 py-2.5 rounded-full shadow-lg group-hover:bg-dlh-orange group-hover:text-white transition-colors">Baca Selengkapnya <ArrowRight className="h-4 w-4" /></span>
              <span className="text-white/75 text-xs sm:text-sm flex items-center gap-2"><CalendarDays className="h-4 w-4" />{formatWIT(headline.published_at)}</span>
            </div>
          </div>
        </div>
      </Link>
      {headlines?.length > 1 && (
        <div className="bg-dlh-green text-white overflow-hidden relative">
          <div className="flex items-center">
            <div className="bg-dlh-orange px-4 py-2.5 font-bold text-sm shrink-0 z-10 flex items-center gap-2">HEADLINE</div>
            <button onClick={() => setPaused((p) => !p)} data-testid="marquee-pause-btn"
              className="px-3 py-2.5 bg-dlh-greenDark hover:bg-black/30 shrink-0 z-10 focus:outline-none focus:ring-2 focus:ring-dlh-orange" aria-label={paused ? "Jalankan" : "Jeda"}>
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </button>
            <div className="overflow-hidden flex-1">
              <div className={`marquee-track ${paused ? "paused" : ""} py-2.5`}>
                {[...headlines, ...headlines].map((h, i) => (
                  <Link key={i} to={`/berita/${h.slug}`} className="mx-6 text-sm hover:text-dlh-greenLight">• {h.title}</Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function SectionTitle({ children, to, linkLabel }) {
  return (
    <div className="flex items-end justify-between mb-6 gap-4">
      <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-dlh-green relative pl-4 border-l-4 border-dlh-orange">{children}</h2>
      {to && <Link to={to} className="text-sm font-semibold text-dlh-greenMid hover:text-dlh-orange flex items-center gap-1 shrink-0">{linkLabel} <ArrowRight className="h-4 w-4" /></Link>}
    </div>
  );
}

export default function Home() {
  const ctx = useOutletContext();
  const unitMap = ctx?.unitMap || {};
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/public/home").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-gray-500">Memuat portal…</div>;

  return (
    <div>
      <Hero headline={data.headline} headlines={data.headlines} unitMap={unitMap} />

      {/* 2. Berita Terbaru */}
      <section className="max-w-7xl mx-auto px-4 py-12" data-testid="latest-section">
        <SectionTitle to="/berita" linkLabel="Lihat Semua Berita">Berita Terbaru</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {data.latest.map((a) => <NewsCard key={a.id} article={a} unitMap={unitMap} />)}
        </div>
        {data.latest.length === 0 && <p className="text-gray-500">Belum ada berita.</p>}
      </section>

      {/* 3. Kegiatan Sekretariat & Bidang */}
      <section className="bg-dlh-greenPale py-12" data-testid="by-unit-section">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle>Kegiatan Sekretariat dan Bidang</SectionTitle>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {data.by_unit.map((b) => (
              <div key={b.unit.slug} className="bg-white rounded-lg border border-gray-200 p-5">
                <Link to={`/bidang/${b.unit.slug}`} className="font-serif font-bold text-dlh-green hover:text-dlh-orange leading-snug block mb-3">{b.unit.name}</Link>
                {b.articles.length === 0 && <p className="text-sm text-gray-400">Belum tersedia berita terbit.</p>}
                <ul className="space-y-3">
                  {b.articles.map((a) => (
                    <li key={a.id}>
                      <Link to={`/berita/${a.slug}`} className="flex gap-3 group">
                        <img src={a.featured_media_url ? mediaUrl(a.featured_media_url) : BRAND.hero} alt="" loading="lazy" className="w-16 h-16 rounded object-cover shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-gray-800 group-hover:text-dlh-orange line-clamp-2 leading-snug">{a.title}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatWIT(a.published_at)}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Sorotan PAPA BERSERI */}
      <section className="max-w-7xl mx-auto px-4 py-12" data-testid="papa-berseri-section">
        <div className="rounded-2xl bg-dlh-green text-white p-6 sm:p-10 overflow-hidden relative">
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2"><Leaf className="h-6 w-6 text-dlh-greenLight" /><span className="text-dlh-greenLight font-bold uppercase tracking-widest text-sm">Sorotan Program</span></div>
            <h2 className="font-serif text-3xl sm:text-4xl font-extrabold">PAPA BERSERI</h2>
            <p className="text-white/80 mt-2 max-w-2xl">Pilah, Angkut, Pengolahan, Proses Akhir — gerakan pengelolaan sampah Kabupaten Mimika.</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              {PILLARS.map((p) => {
                const Icon = p.icon;
                return (
                  <Link key={p.key} to={`/berita?program=${encodeURIComponent(p.key)}`} data-testid={`papa-pillar-${p.key}`}
                    className="bg-white/10 hover:bg-dlh-greenLight/20 border border-white/15 rounded-xl p-5 transition-colors group focus:outline-none focus:ring-2 focus:ring-dlh-orange">
                    <Icon className="h-8 w-8 text-dlh-greenLight mb-3 group-hover:scale-110 transition-transform" />
                    <div className="font-serif font-bold text-lg">{p.key}</div>
                    <div className="text-sm text-white/70">{p.desc}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
        {data.papa_berseri.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {data.papa_berseri.slice(0, 3).map((a) => <NewsCard key={a.id} article={a} unitMap={unitMap} />)}
          </div>
        )}
      </section>

      {/* 5. Edukasi & Informasi Publik */}
      <section className="bg-dlh-greenPale py-12" data-testid="edukasi-section">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle to="/berita?content_type=edukasi" linkLabel="Selengkapnya">Edukasi dan Informasi Publik</SectionTitle>
          {data.edukasi.length === 0 ? <p className="text-gray-500">Belum tersedia.</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.edukasi.map((a) => <NewsCard key={a.id} article={a} unitMap={unitMap} />)}
            </div>
          )}
        </div>
      </section>

      {/* 6. Foto & Video Terbaru */}
      <section className="max-w-7xl mx-auto px-4 py-12" data-testid="media-section">
        <SectionTitle to="/galeri" linkLabel="Buka Galeri">Foto dan Video Terbaru</SectionTitle>
        {data.galleries.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-gray-500">
            <Camera className="h-10 w-10 mx-auto mb-3 text-gray-300" /> Dokumentasi galeri belum tersedia.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.galleries.map((g) => (
              <Link key={g.id} to="/galeri" className="relative rounded-lg overflow-hidden aspect-square bg-gray-100 group">
                <img src={g.items?.[0] ? mediaUrl(g.items[0].thumbnail || g.items[0].url || g.items[0]) : BRAND.hero} alt={g.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-3">
                  <div className="text-white">
                    <div className="flex items-center gap-1 text-xs">{g.type === "video" ? <Film className="h-3 w-3" /> : <Camera className="h-3 w-3" />}{g.type === "video" ? "Video" : "Foto"}</div>
                    <p className="text-sm font-semibold line-clamp-2">{g.title}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
